import { type ReactNode } from 'react';

import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useOrgMembers } from '@/lib/portfolio/hooks/useOrgMembers';
import { useOrgTeams } from '@/lib/portfolio/hooks/useOrgTeams';

import { MembersTable } from './MembersTable';
import { OrgInvitationsSection } from './OrgInvitationsSection';

interface OrgMembersSectionProps {
  portfolioId: number;
  orgId: number;
  orgName: string;
}

export function OrgMembersSection({
  portfolioId,
  orgId,
  orgName,
}: OrgMembersSectionProps) {
  const membersQuery = useOrgMembers(portfolioId, orgId);
  const teamsQuery = useOrgTeams(portfolioId, orgId);
  const hasTeamsError = teamsQuery.isError;

  const members = membersQuery.data ?? [];

  let body: ReactNode;
  if (membersQuery.isLoading) {
    body = <LoadingIndicator size='sm' />;
  } else if (membersQuery.isError) {
    body = (
      <EmptyState
        variant='default'
        title="Couldn't load members"
        description={`There was a problem loading members for ${orgName}.`}
      />
    );
  } else if (members.length === 0) {
    body = <EmptyState variant='empty' title='No members' />;
  } else {
    body = (
      <MembersTable
        members={members}
        teams={teamsQuery.data ?? []}
        portfolioId={portfolioId}
        orgId={orgId}
      />
    );
  }

  return (
    <section>
      {/* The heading stays the org name: HorizontalTabs hides itself for a
          single-org portfolio, so this is the only place that org is named.
          The line under it states the governance ceiling — there is no remove
          control here, and saying so turns an apparent gap into a policy. */}
      <h3 className='mb-1 text-heading-4 text-[var(--color-text-primary)]'>
        {orgName}
      </h3>
      <p className='mb-3 text-body-small text-[var(--color-text-secondary)]'>
        Set roles and teams here. Removing people stays with {orgName}.
      </p>
      {body}
      {hasTeamsError && (
        <p className='mt-2 text-body-small text-[var(--color-text-tertiary)]'>
          Teams unavailable
        </p>
      )}
      <OrgInvitationsSection
        portfolioId={portfolioId}
        orgId={orgId}
        orgName={orgName}
      />
    </section>
  );
}
